import translate from '@allesauto/i18n';
import {
  BenefitBackground,
  FieldTypeHTML,
  FieldTypeLink,
  ListMarkerType,
  TextComponentTypes,
} from '@allesauto/utils/consts';
import { createMarkup, getUrlFromStoryblokLink } from '@allesauto/utils/funcs';
import { SCREEN_BREAKPOINT, useScreenBreakpoint } from '@allesauto/utils/hooks';
import {
  ITypeListMarker,
  ITypeListMarkerColor,
} from '@allesauto/utils/storyblok';
import { Add16, Subtract16 } from '@carbon/icons-react';
import { renderRichText } from '@storyblok/react';
import clsx from 'clsx';
import React, {
  ReactNode,
  RefObject,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

import './accordion-item.css';

import AnimateHeight from '../../animateHeight';
import CheckList from '../../checklistContainer/checklist/CheckList';
import { TextComponentInterface } from '../../textComponent';
import TextLink from '../../textLink';
import { TextComponentLegacy } from '../../../index';

export type AccordionItemProps = AccordionItemWithHtml | AccordionWithChildren;

interface AccordionItemWithHtml extends AccordionItemBase {
  childHtml: FieldTypeHTML | string;
  children?: never;
}

interface AccordionWithChildren extends AccordionItemBase {
  children?: ReactNode;
  childHtml?: never;
}

interface AccordionItemBase {
  _uid: string;
  title: string;
  isOpen?: boolean;
  isNarrow?: boolean;
  onToggle?: (id: string, ref?: RefObject<HTMLDivElement>) => void;
  context?: 'header';
  tabIndex?: number;
  listItems?: string[];
  listBackground?: BenefitBackground;
  listMarkerType?: ListMarkerType;
  link?: FieldTypeLink;
  linkText?: string;
  testId?: string;
  fullScreen?: boolean;
  /** @deprecated use the childHtml field to render richtext within an item */
  textComponent?: TextComponentInterface[];
  isDefaultOpen?: boolean;
  listItemMarker?: ITypeListMarker;
  listItemMarkerColor?: ITypeListMarkerColor;
}

const MOBILE_HEIGHT_RESTRICTION = 400;

const AccordionItem: React.FC<React.PropsWithChildren<AccordionItemProps>> = (
  props: AccordionItemProps
) => {
  const {
    _uid,
    title,
    children,
    childHtml,
    isOpen = false,
    context = '',
    isNarrow = false,
    onToggle,
    tabIndex = 0,
    listItems,
    listBackground = BenefitBackground.WHITE,
    listMarkerType = ListMarkerType.BULLET,
    link,
    linkText,
    testId,
    textComponent,
    fullScreen,
    isDefaultOpen = false,
    listItemMarker,
    listItemMarkerColor,
  } = props;

  const ref = useRef<HTMLDivElement>(null);
  const [showMore, setShowMore] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const linkUrl = link ? getUrlFromStoryblokLink(link) : undefined;

  const intl = useIntl();
  const childrenRef = useRef<HTMLDivElement>(null);
  const breakpoint = useScreenBreakpoint();

  const mobileBreakpoint = breakpoint < SCREEN_BREAKPOINT.MD;

  useEffect(() => {
    if (mobileBreakpoint && isOpen && childrenRef?.current) {
      const paragraphLength =
        childrenRef.current.getElementsByTagName('p').length;
      const listLength = childrenRef.current.getElementsByTagName('ul').length;
      const containerLength =
        childrenRef.current.getElementsByTagName('div').length;
      const containerHeight =
        childrenRef.current.getElementsByTagName('div')[0]?.clientHeight;

      if (containerLength >= 1 && containerHeight > MOBILE_HEIGHT_RESTRICTION) {
        setShowMore(true);
      } else if (paragraphLength > 1 && listLength > 0) {
        const paragraphs: any = childrenRef.current.getElementsByTagName('p');
        const listHeight =
          childrenRef.current.getElementsByTagName('ul')[0].clientHeight;

        let paragraphsHeight = 0;

        for (const paragraph of paragraphs) {
          paragraphsHeight += paragraph.clientHeight;
        }

        const totalHeight = paragraphsHeight + listHeight;
        totalHeight > MOBILE_HEIGHT_RESTRICTION && setShowMore(true);
      } else {
        const paragraphHeight =
          childrenRef.current.getElementsByTagName('p')[0]?.clientHeight;
        paragraphHeight > MOBILE_HEIGHT_RESTRICTION && setShowMore(true);
      }
    }

    if (!mobileBreakpoint) {
      setShowMore(false);
    }
  }, [isOpen, mobileBreakpoint]);

  useEffect(() => {
    // make links within accessible only when accordion is Open
    if (childrenRef?.current) {
      const anchorTags = childrenRef.current.querySelectorAll('a');
      anchorTags.forEach((a) => (a.tabIndex = isOpen ? tabIndex : -1));
    }
  }, [isOpen, tabIndex, childHtml]);

  const className = {
    accordionItem: clsx(
      'accordion-item aurego-accordion-item border border-b-1px border-b-grey30 group leading-5 relative text-p1',
      context === 'header' &&
        'border-b-grey100 cursor-default hover:border-b-1px'
    ),
    accordionBorder: clsx(
      !isOpen &&
        'absolute bottom-[-1px] bg-green60 aurego-accordion-border monochrome:bg-grey90 wl-bg-highlight duration-[200ms] ease-linear h-[7px] scale-y-0 origin-bottom transition-all w-full',
      context !== 'header' && ' group-hover:scale-y-100'
    ),
    accordionHeader: clsx(
      'accordion-header flex font-headline font-weight-bold items-center justify-between py-20px relative text-left text-purple80 transition-all w-full md:py-25px monochrome:text-grey90',
      !isNarrow && 'lg:py-35px lg:text-headline4 lg:leading-6 ',
      context === 'header' && 'text-headline3 leading-7 !py-30px cursor-pointer'
    ),
    accordionContentWrapper: clsx(
      'font-weight-normal grid grid-cols-22 leading-p1 md:max-h-full',
      listItems ? 'pb-10px' : 'pb-30px',
      context !== 'header' && 'accordion-content',
      !children && 'sm:max-h-[400px] sm:overflow-hidden',
      !children && showMore && expanded && 'sm:max-h-fit'
    ),
    accordionContent: clsx(
      childHtml &&
        `rich-text rich-text-list-marker-${
          listItemMarker ? listItemMarker : 'bullet'
        }-${listItemMarkerColor ? listItemMarkerColor : 'purple'}`,
      !fullScreen && 'md:col-start-1 md:col-end-21',
      context === 'header' && 'col-end-23 col-start-3'
    ),
    showMoreContainer: clsx(
      'cursor-pointer mb-40px',
      !children && showMore && !expanded && 'mt-20px'
    ),
    showMoreIcon: clsx(
      'inline-block text-purple80 monochrome:text-grey90',
      context === 'header' && '!text-white'
    ),
    showMoreText: clsx(
      'inline-block leading-5 ml-[9px] text-p1 text-purple80 underline monochrome:text-grey90',
      context === 'header' && '!text-white'
    ),
    accordionIconContainer: clsx(
      'ml-20px px-[6px] relative md:ml-30px md:px-[8px]',
      !isNarrow && 'lg:ml-40px lg:px-10px',
      context === 'header' && 'mr-5px'
    ),
    accordionIconParent: 'relative p-[inherit] m-[inherit]',
    accordionIcon: clsx(
      "absolute accordion-icon after:absolute after:bg-purple80 after:content-[''] after:duration-300 after:h-[2px] after:md:w-[16px] after:mt-[-1px] after:rounded-[10px] after:top-1/2 after:transition-transform after:w-[12px] before:absolute before:bg-purple80 before:content-[''] before:h-[12px] before:left-1/2 before:md:h-[16px] before:ml-[-1px] before:rounded-[10px] before:w-[2px] duration-300 h-[12px] ml-[-8px] mt-[-8px] right-0 top-1/2 transition-transform w-[12px] md:h-[16px] md:w-[16px] monochrome:after:bg-grey90 monochrome:before:bg-grey90",
      isOpen && 'minus after:rotate-[-90deg] rotate-[-90deg]',
      !isNarrow && 'lg:h-20px lg:w-20px before:lg:h-20px after:lg:w-20px',
      context === 'header' &&
        '!h-10px !w-10px after:!w-10px before:!h-10px after:!bg-white before:!bg-white'
    ),
  };

  const {
    accordionItem,
    accordionBorder,
    accordionHeader,
    accordionContent,
    accordionIcon,
    accordionIconContainer,
    accordionIconParent,
    accordionContentWrapper,
  } = className;

  return (
    <div
      className={accordionItem}
      aria-expanded={isOpen || isDefaultOpen}
      ref={ref}
    >
      <button
        className={accordionHeader}
        tabIndex={tabIndex}
        onClick={() => onToggle && onToggle(_uid, ref)}
        id={testId}
      >
        <h3 className="text-headline4">{title}</h3>
        <div className={accordionIconContainer}>
          <div className={accordionIconParent}>
            <div className={accordionIcon} />
          </div>
        </div>
      </button>
      <AnimateHeight show={isOpen || isDefaultOpen} duration={400}>
        {/* TODO The textComponentLegacy will not be child of accordion model in Storyblok */}
        {textComponent && (
          <TextComponentLegacy
            textComponents={textComponent}
            className="mb-30px sm:!pt-0 md:!pt-0"
            type={TextComponentTypes.ACCORDION}
          ></TextComponentLegacy>
        )}
        <div className={accordionContentWrapper}>
          <div
            ref={childrenRef}
            className={accordionContent}
            {...(childHtml && {
              dangerouslySetInnerHTML: createMarkup(
                (childHtml as unknown as FieldTypeHTML).type
                  ? renderRichText(childHtml as FieldTypeHTML)
                  : (childHtml as unknown as string)
              ),
            })}
          >
            {children}
          </div>
        </div>
        {listItems && (
          <CheckList
            list={{
              listItems: listItems,
              background: listBackground,
              markerType: listMarkerType,
              customClassName: {
                wrapperClassName: 'lg:p-0 lg:pb-20px',
              },
            }}
          />
        )}
        {linkUrl && linkText && (
          <TextLink
            text={linkText}
            href={linkUrl}
            target={link?.target ?? '_self'}
            customClassName={'mb-40px'}
          />
        )}
        {!children && showMore && (
          <div
            className={className.showMoreContainer}
            onClick={() => setExpanded(!expanded)}
          >
            {(expanded && (
              <Subtract16 className={className.showMoreIcon} />
            )) || <Add16 className={className.showMoreIcon} />}
            <span className={className.showMoreText}>
              {expanded
                ? translate(intl, 'ui.ACCORDION_ITEM.showLess')
                : translate(intl, 'ui.ACCORDION_ITEM.showMore')}
            </span>
          </div>
        )}
      </AnimateHeight>
      <div className={accordionBorder} />
    </div>
  );
};

export default AccordionItem;
